import type { Product, ProductVariant } from '@/types';

// Price (in cents) for a given variant: applies the variant's priceModifier to the
// effective base (salePrice if present, else basePrice). MVP-level: per-variant sales
// are not modeled separately (a DRB-3 refinement) — the product-level salePrice is the headline.
export function variantPrice(product: Product, variant: ProductVariant): number {
  const base = product.salePrice ?? product.basePrice;
  return Math.max(0, base + variant.priceModifier);
}

export function variantRegularPrice(product: Product, variant: ProductVariant): number {
  return Math.max(0, product.basePrice + variant.priceModifier);
}

export function isOnSale(product: Product): boolean {
  return typeof product.salePrice === 'number' && product.salePrice < product.basePrice;
}

export function defaultVariant(product: Product): ProductVariant {
  return product.variants[0];
}

export function findVariant(product: Product, variantId: string): ProductVariant {
  return product.variants.find(v => v.id === variantId) ?? product.variants[0];
}

// Subscribe & Save price for a variant.
export function subscribePrice(product: Product, variant: ProductVariant): number {
  return Math.round(variantPrice(product, variant) * (1 - product.subscribeDiscount));
}

export function primaryImage(product: Product): string {
  return product.images[product.thumbnailIndex] ?? product.images[0] ?? '';
}

// Curated bestsellers for the homepage. Badge is single-valued (semantic: DAYTIME/SALE/etc.),
// so bestsellers are an explicit curated set rather than a badge filter.
const BESTSELLER_SLUGS = [
  'cbd-pure-1200mg',
  'cbd-muscle-joint-ointment',
  'cbd-pain-awake-1200mg',
  'cbd-pain-sleep-1200mg',
  'cbd-migraine-600mg',
];

export function getBestsellers(products: Product[], n = 5): Product[] {
  const bySlug = new Map(products.map(p => [p.slug, p]));
  const curated = BESTSELLER_SLUGS.map(s => bySlug.get(s)).filter((p): p is Product => Boolean(p));
  if (curated.length >= n) return curated.slice(0, n);
  // backfill with highest-rated remaining
  const rest = products
    .filter(p => !BESTSELLER_SLUGS.includes(p.slug))
    .sort((a, b) => b.ratingsCount - a.ratingsCount);
  return [...curated, ...rest].slice(0, n);
}

export function relatedProducts(products: Product[], current: Product, n = 3): Product[] {
  return products
    .filter(p => p.id !== current.id && p.category === current.category)
    .slice(0, n)
    .concat(products.filter(p => p.id !== current.id && p.category !== current.category))
    .slice(0, n);
}
